import React, { useState, useEffect } from 'react'
import Layout from '../../../../components/vision/Layout'
import ProtectedRoute from '../../../../components/vision/ProtectedRoute'
import type { SegmentoCliente } from '../../../../lib/supabase'

const SegmentosPage: React.FC = () => {
  const [segmentos, setSegmentos] = useState<SegmentoCliente[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingSegmento, setEditingSegmento] = useState<SegmentoCliente | null>(null)
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    icone: '',
    ordem: 0
  })

  useEffect(() => {
    fetchSegmentos()
  }, [])

  const fetchSegmentos = async () => {
    try {
      setLoading(true)
      const res = await fetch('/api/gestao/segmentos')
      if (res.ok) {
        const data = await res.json()
        setSegmentos(data)
      }
    } catch (error) {
      console.error('Erro ao buscar segmentos:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleOpenModal = (segmento?: SegmentoCliente) => {
    if (segmento) {
      setEditingSegmento(segmento)
      setFormData({
        nome: segmento.nome,
        descricao: segmento.descricao || '',
        icone: segmento.icone || '',
        ordem: segmento.ordem
      })
    } else {
      setEditingSegmento(null)
      setFormData({
        nome: '',
        descricao: '',
        icone: '',
        ordem: segmentos.length + 1
      })
    }
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingSegmento(null)
    setFormData({ nome: '', descricao: '', icone: '', ordem: 0 })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      if (editingSegmento) {
        // Editar
        const res = await fetch('/api/gestao/segmentos', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: editingSegmento.id,
            ...formData
          })
        })

        if (res.ok) {
          alert('✅ Segmento atualizado com sucesso!')
          fetchSegmentos()
          handleCloseModal()
        } else {
          alert('❌ Erro ao atualizar segmento')
        }
      } else {
        // Criar
        const res = await fetch('/api/gestao/segmentos', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        })

        if (res.ok) {
          alert('✅ Segmento criado com sucesso!')
          fetchSegmentos()
          handleCloseModal()
        } else {
          alert('❌ Erro ao criar segmento')
        }
      }
    } catch (error) {
      console.error('Erro ao salvar segmento:', error)
      alert('❌ Erro ao salvar segmento')
    }
  }

  const handleToggleAtivo = async (segmento: SegmentoCliente) => {
    try {
      const res = await fetch('/api/gestao/segmentos', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: segmento.id,
          ativo: !segmento.ativo
        })
      })

      if (res.ok) {
        alert(`✅ Segmento ${!segmento.ativo ? 'ativado' : 'desativado'} com sucesso!`)
        fetchSegmentos()
      } else {
        alert('❌ Erro ao alterar status do segmento')
      }
    } catch (error) {
      console.error('Erro ao alterar status:', error)
      alert('❌ Erro ao alterar status do segmento')
    }
  }

  return (
    <ProtectedRoute>
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent mb-2">
                  Segmentos de Clientes
                </h1>
                <p className="text-gray-600">
                  Gerencie os tipos de negócio disponíveis para cadastro de clientes
                </p>
              </div>
              <button
                onClick={() => handleOpenModal()}
                className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>Novo Segmento</span>
              </button>
            </div>
          </div>

          {/* Listagem */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {loading ? (
              <div className="p-12 text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
                <p className="mt-4 text-gray-600">Carregando segmentos...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-orange-50 to-amber-50 border-b border-orange-100">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-orange-900 uppercase tracking-wider">
                        Ordem
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-orange-900 uppercase tracking-wider">
                        Ícone
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-orange-900 uppercase tracking-wider">
                        Nome
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-orange-900 uppercase tracking-wider">
                        Descrição
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-orange-900 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-semibold text-orange-900 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {segmentos.map((segmento) => (
                      <tr key={segmento.id} className="hover:bg-orange-50 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {segmento.ordem}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-2xl">
                          {segmento.icone}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                          {segmento.nome}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 max-w-md truncate">
                          {segmento.descricao}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            segmento.ativo 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {segmento.ativo ? '✓ Ativo' : '✗ Inativo'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                          <button
                            onClick={() => handleOpenModal(segmento)}
                            className="text-blue-600 hover:text-blue-900 font-semibold"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => handleToggleAtivo(segmento)}
                            className={`font-semibold ${
                              segmento.ativo 
                                ? 'text-red-600 hover:text-red-900' 
                                : 'text-green-600 hover:text-green-900'
                            }`}
                          >
                            {segmento.ativo ? 'Desativar' : 'Ativar'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Modal de Criação/Edição */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
              {/* Header */}
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-b border-orange-100 px-6 py-4">
                <h2 className="text-2xl font-bold text-orange-900">
                  {editingSegmento ? 'Editar Segmento' : 'Novo Segmento'}
                </h2>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nome do Segmento *
                  </label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Ex: Academia/Fitness"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Descrição
                  </label>
                  <textarea
                    value={formData.descricao}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Ex: Academias, estúdios de pilates, crossfit, personal trainer"
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Ícone (Emoji)
                    </label>
                    <input
                      type="text"
                      value={formData.icone}
                      onChange={(e) => setFormData({ ...formData, icone: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-2xl text-center"
                      placeholder="💪"
                      maxLength={2}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Cole um emoji aqui
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Ordem de Exibição *
                    </label>
                    <input
                      type="number"
                      value={formData.ordem}
                      onChange={(e) => setFormData({ ...formData, ordem: parseInt(e.target.value) })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      min="1"
                      required
                    />
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-6 py-3 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                  >
                    {editingSegmento ? 'Salvar Alterações' : 'Criar Segmento'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </Layout>
    </ProtectedRoute>
  )
}

export default SegmentosPage
